import { get } from '@direct-frontend/stdlib';
import { logEventString, type TDict } from '@adv-frontend/yastore-modules/logger';
import { isRequestError, isSchemaProblemError } from '#shared/lib/requests';
import { showError } from '#shared/lib/toast';
import { validateIntegrationCredentials } from '../api';

function isBadRequestError(error: unknown) {
    return get(error, ['responseData', 'code']) === 'bad_request';
}

export type IntegrationValidationFailReason =
    | 'merchant_account_error'
    | 'invalid_credentials'
    | 'bad_request'
    | 'unknown';

const INVALID_KEY_REASONS: IntegrationValidationFailReason[] = ['invalid_credentials', 'bad_request'];

export const logIntegrationInvalidKeyOnFail =
    (event: string, meta?: TDict) => (reason: IntegrationValidationFailReason) => {
        if (INVALID_KEY_REASONS.includes(reason)) {
            logEventString(event, reason, meta);
        }
    };

interface HandleValidateIntegrationErrorOptions {
    onFail?: (reason: IntegrationValidationFailReason) => void;
}

export const handleValidateIntegrationError = async (
    request: Parameters<typeof validateIntegrationCredentials>[0],
    { onFail }: HandleValidateIntegrationErrorOptions = {}
) => {
    try {
        await validateIntegrationCredentials(request);
    } catch (error) {
        if (isSchemaProblemError(error)) {
            if (request.params.type === 'yandex-pay' && error.responseData.type === 'merchant_account_error') {
                onFail?.('merchant_account_error');
                showError(
                    { label: 'Ошибка аккаунта Яндекс Пэй', durationMs: 10_000 },
                    'Для подключения сервиса необходимо завершить настройку аккаунта Яндекс Пэй'
                );

                return false;
            }

            if (error.responseData.type === 'invalid_credentials') {
                onFail?.('invalid_credentials');
                showError(
                    { label: 'Некорректный ключ', durationMs: 10_000 },
                    'Для подключения сервиса необходимо указать корректные значения ключей'
                );

                return false;
            }
        }

        if (isBadRequestError(error)) {
            onFail?.('bad_request');
            showError(
                { label: 'Некорректный ключ', durationMs: 10_000 },
                'Для подключения сервиса необходимо указать корректные значения ключей'
            );

            return false;
        }

        if (
            isRequestError(error) &&
            request.params.type === 'yandex-pay' &&
            error.responseData.message.includes('MERCHANT_ACCOUNT_ERROR')
        ) {
            onFail?.('merchant_account_error');
            showError(
                { label: 'Ошибка аккаунта Яндекс Пэй', durationMs: 10_000 },
                'Для подключения сервиса необходимо завершить настройку аккаунта Яндекс Пэй'
            );

            return false;
        }

        onFail?.('unknown');
        showError({ label: 'Произошла ошибка при подключении сервиса' });

        return false;
    }

    return true;
};
